import { motion } from "framer-motion";
import { BadgeCheck, Bot, PawPrint, User } from "lucide-react";
import { cn } from "@/lib/utils";

/* ---------- Etapa 1: Anúncio ---------- */
export const MockAd = ({ clicked }: { clicked: boolean }) => (
  <div className="flex h-full items-center justify-center p-6">
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative w-full max-w-xs overflow-hidden rounded-xl border border-ring/20 bg-[hsl(var(--terminal-card))] shadow-2xl"
    >
      {/* "Imagem" do anúncio */}
      <div className="relative flex h-32 items-center justify-center bg-gradient-to-br from-[hsl(var(--hero-gradient-start))] to-[hsl(var(--hero-gradient-end))]">
        <PawPrint size={44} className="text-primary-foreground/90" />
        <span className="absolute left-3 top-3 rounded bg-black/40 px-2 py-0.5 text-[10px] font-medium text-primary-foreground/90">
          Patrocinado
        </span>
      </div>
      <div className="space-y-2 p-4">
        <p className="font-heading text-sm font-bold text-[hsl(var(--terminal-foreground))]">
          Ração Premium com 30% OFF
        </p>
        <p className="text-xs text-[hsl(var(--terminal-foreground))]/60">
          Nutrição completa para o seu melhor amigo. Só esta semana!
        </p>
        <div className="relative mt-3">
          <div className="rounded-lg bg-primary py-2 text-center text-xs font-bold text-primary-foreground">
            Saiba mais
          </div>
          {/* Pulso de clique simulado */}
          {clicked && (
            <motion.span
              initial={{ opacity: 0.8, scale: 0.6 }}
              animate={{ opacity: 0, scale: 1.8 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="absolute inset-0 rounded-lg border-2 border-ring"
            />
          )}
        </div>
      </div>
    </motion.div>
  </div>
);

/* ---------- Etapa 2: Chatbot IA ---------- */
const chatMessages = [
  { from: "user" as const, text: "Oi! Vi o anúncio da ração premium" },
  {
    from: "bot" as const,
    text: "Olá, Mariana! Sou a IA da Abillyting. Qual o porte do seu cão? Vou montar o plano ideal.",
  },
  { from: "user" as const, text: "Porte médio, uns 12kg" },
  {
    from: "bot" as const,
    text: "Perfeito! Plano Mensal Premium — R$ 189,90/mês com 30% OFF. Pedido confirmado ✓",
  },
];

export const MockChat = ({
  visibleCount,
  typing,
}: {
  visibleCount: number;
  typing: boolean;
}) => (
  <div className="flex h-full flex-col justify-center p-6">
    <div className="mx-auto w-full max-w-sm space-y-3">
      {chatMessages.slice(0, visibleCount).map((msg, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 14, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className={cn("flex items-end gap-2", msg.from === "user" && "flex-row-reverse")}
        >
          <span
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
              msg.from === "bot" ? "bg-primary text-primary-foreground" : "bg-ring/25 text-ring"
            )}
          >
            {msg.from === "bot" ? <Bot size={14} /> : <User size={14} />}
          </span>
          <p
            className={cn(
              "max-w-[75%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed",
              msg.from === "bot"
                ? "rounded-bl-sm bg-primary/90 text-primary-foreground"
                : "rounded-br-sm bg-[hsl(var(--terminal-card))] text-[hsl(var(--terminal-foreground))] border border-ring/15"
            )}
          >
            {msg.text}
          </p>
        </motion.div>
      ))}

      {/* Indicador "digitando..." */}
      {typing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Bot size={14} />
          </span>
          <span className="flex gap-1 rounded-2xl rounded-bl-sm bg-primary/40 px-3.5 py-2.5">
            {[0, 1, 2].map((d) => (
              <motion.span
                key={d}
                animate={{ opacity: [0.25, 1, 0.25] }}
                transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                className="h-1.5 w-1.5 rounded-full bg-primary-foreground"
              />
            ))}
          </span>
        </motion.div>
      )}
    </div>
  </div>
);

/* ---------- Etapa 3: Registro no PDV / Fluxo de Caixa ---------- */
export const MockLedger = () => (
  <div className="flex h-full items-center justify-center p-6">
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="w-full max-w-sm overflow-hidden rounded-xl border border-ring/20 bg-[hsl(var(--terminal-card))] shadow-2xl"
    >
      <div className="flex items-center justify-between border-b border-ring/10 px-4 py-2.5">
        <p className="text-xs font-semibold text-[hsl(var(--terminal-foreground))]">
          Abilly PDV · Fluxo de Caixa
        </p>
        <span className="rounded-full bg-ring/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ring">
          Ao vivo
        </span>
      </div>

      <div className="space-y-2 p-4">
        {/* Linha da venda */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="flex items-center justify-between rounded-lg border border-ring/15 bg-[hsl(var(--terminal))] px-3 py-2.5"
        >
          <div>
            <p className="text-xs font-semibold text-[hsl(var(--terminal-foreground))]">
              Venda #1042 · Plano Mensal Premium
            </p>
            <p className="text-[10px] text-[hsl(var(--terminal-foreground))]/50">
              Mariana S. · via Chatbot IA
            </p>
          </div>
          <p className="text-sm font-bold text-ring">R$ 189,90</p>
        </motion.div>

        {/* Status confirmado */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8, duration: 0.4 }}
          className="flex items-center gap-2 rounded-lg bg-primary/15 px-3 py-2"
        >
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.9, type: "spring", stiffness: 400, damping: 15 }}
          >
            <BadgeCheck size={16} className="text-ring" />
          </motion.span>
          <p className="text-xs font-medium text-[hsl(var(--terminal-foreground))]">
            Confirmado · estoque e caixa atualizados automaticamente
          </p>
        </motion.div>

        {/* Mini barras de caixa */}
        <div className="flex h-12 items-end gap-1.5 pt-1">
          {[35, 50, 42, 60, 48, 72, 100].map((h, i) => (
            <motion.div
              key={i}
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ delay: 0.5 + i * 0.08, duration: 0.4, ease: "easeOut" }}
              className={cn("flex-1 rounded-t-sm", i === 6 ? "bg-ring" : "bg-primary/40")}
            />
          ))}
        </div>
      </div>
    </motion.div>
  </div>
);
